import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import { confirmCheckout } from '../../../actions/coachCertificationActions'
import { certificationPath, sectionsPath } from '../../../utility/coachCertification'
import NoticeCard from './NoticeCard'

const POLL_INTERVAL = 2000
const POLL_DEADLINE = 30000

const CheckoutPage = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { certificationId } = useParams()
  const { pathname, search } = useLocation()
  const [searchParams] = useSearchParams()
  const [timedOut, setTimedOut] = useState(false)
  const hostedPageId = searchParams.get('id')
  const cancelled = searchParams.get('state') === 'cancelled'

  useEffect(() => {
    if (cancelled || !hostedPageId) {
      if (cancelled) { toast.info(t('portal.certification.checkout.cancelled'), { position: toast.POSITION.TOP_RIGHT }) }
      navigate(certificationPath(certificationId), { replace: true })
      return undefined
    }

    // An expired session signs the coach out mid-poll; bring them back here after they log in again.
    localStorage.setItem('redirectUrl', `${pathname}${search}`)
    const startedAt = Date.now()
    let active = true
    let timer = null
    const leave = (path) => { localStorage.removeItem('redirectUrl'); navigate(path, { replace: true }) }
    const confirm = () => dispatch(confirmCheckout(hostedPageId)).unwrap()
      .then(({ pending }) => {
        if (!active) { return undefined }
        if (!pending) { return leave(sectionsPath(certificationId)) }
        if (Date.now() - startedAt >= POLL_DEADLINE) { return setTimedOut(true) }
        timer = setTimeout(confirm, POLL_INTERVAL)
        return undefined
      })
      .catch((errors) => {
        if (!active || errors?.status === 401) { return }
        toast.error(errors?.base?.join(' ') || errors?.general?.[0] || t('common.somethingWentWrong'), { position: toast.POSITION.TOP_RIGHT })
        leave(certificationPath(certificationId))
      })
    confirm()
    return () => { active = false; clearTimeout(timer) }
  }, [dispatch, navigate, t, certificationId, hostedPageId, cancelled, pathname, search])

  return (
    <NoticeCard
      icon={timedOut ? 'fa fa-clock-o' : 'fa fa-spinner fa-spin'}
      title={t(timedOut ? 'portal.certification.checkout.pendingTitle' : 'portal.certification.checkout.confirming')}
      body={t(timedOut ? 'portal.certification.checkout.pendingBody' : 'portal.certification.checkout.confirmingBody')}
    >
      {timedOut && (
        <Link
          to={certificationPath(certificationId)}
          onClick={() => localStorage.removeItem('redirectUrl')}
          className='btn btn-primary text-white mt-4'
        >
          {t('portal.certification.checkout.pendingLink')}
        </Link>
      )}
    </NoticeCard>
  )
}

export default CheckoutPage
